// src/components/DocumentosFiscais.js
//
// Botões dos documentos fiscais de um chamado: DANFSe (PDF), XML da NFS-e e Boleto.
// Lê do banco "financ": cobrancas/{ticketId} (vínculos com o VHSYS) e nfse/{chave} (XML oficial).
// Não aparece quando o chamado não tem cobrança com NF nem boleto.

import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { dbFinanc, auth } from '../App';
import { printDanfse, downloadXml } from '../utils/danfse';

const Icon = ({ d }) => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={d} /></svg>
);
const ICON_PDF = 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
const ICON_XML = 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4';
const ICON_BOLETO = 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z';
const ICON_SYNC = 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15';

export default function DocumentosFiscais({ ticketId, className = '' }) {
    const [cob, setCob] = useState(undefined); // undefined = carregando, null = não existe
    const [nf, setNf] = useState(null);
    const [nfErro, setNfErro] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null); // { text, type }

    useEffect(() => {
        if (!ticketId) return undefined;
        setCob(undefined);
        const unsub = onSnapshot(doc(dbFinanc, 'cobrancas', ticketId),
            snap => setCob(snap.exists() ? { id: snap.id, ...snap.data() } : null),
            () => setCob(null));
        return () => unsub();
    }, [ticketId]);

    const chave = cob?.nfseChave || '';
    useEffect(() => {
        if (!chave) { setNf(null); setNfErro(''); return undefined; }
        const unsub = onSnapshot(doc(dbFinanc, 'nfse', chave),
            snap => { setNf(snap.exists() ? snap.data() : null); setNfErro(''); },
            err => {
                setNf(null);
                setNfErro(err?.code === 'permission-denied'
                    ? 'Sem permissão para ler o XML da nota: libere a coleção "nfse" nas regras do banco financ.'
                    : `Não foi possível ler o XML da nota: ${err?.message || err}`);
            });
        return () => unsub();
    }, [chave]);

    if (!cob) return null;
    const temNota = !!(cob.vhsysNfseId || cob.vhsysRpsNumero || cob.nfseChave);
    const boleto = cob.vhsysLinkBoleto || '';
    if (!temNota && !boleto) return null;

    const flash = (text, type = 'ok') => { setMsg({ text, type }); setTimeout(() => setMsg(null), type === 'error' ? 7000 : 4000); };

    const buscarNota = async () => {
        setBusy(true);
        try {
            const res = (await httpsCallable(getFunctions(), 'nfseBaixarDocumento')({ cobrancaId: ticketId })).data;
            if (res?.success) flash(`NF ${res.numero || ''} obtida da NFS-e Nacional.`);
            else flash(res?.motivo || 'Não foi possível obter a nota.', 'error');
        } catch (err) {
            flash(err?.code === 'functions/not-found' ? 'Função de NFS-e ainda não publicada.' : `Erro: ${err?.message || err}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    const btn = 'inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
    const xml = nf?.xml || '';

    return (
        <div className={`flex flex-wrap items-center gap-2 print:hidden ${className}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1">
                Documentos fiscais{cob.nfseNumero ? ` · NF ${cob.nfseNumero}` : (cob.vhsysRpsNumero ? ` · RPS ${cob.vhsysRpsNumero}` : '')}
            </span>
            {temNota && (
                <>
                    <button
                        disabled={!xml}
                        onClick={() => printDanfse(xml, { emittedBy: auth.currentUser?.email || '' }).then(ok => { if (ok === false) flash('O navegador bloqueou a janela. Libere pop-ups para este site.', 'error'); })}
                        className={`${btn} border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20`}
                        title={xml ? 'Abrir o DANFSe para imprimir ou salvar em PDF' : 'XML da nota ainda não obtido'}
                    >
                        <Icon d={ICON_PDF} /> DANFSe
                    </button>
                    <button
                        disabled={!xml}
                        onClick={() => downloadXml(xml, chave)}
                        className={`${btn} border-slate-500/40 bg-slate-500/10 text-slate-200 hover:bg-slate-500/20`}
                        title={xml ? 'Baixar o XML oficial da NFS-e' : 'XML da nota ainda não obtido'}
                    >
                        <Icon d={ICON_XML} /> XML
                    </button>
                </>
            )}
            {boleto && (
                <a href={boleto} target="_blank" rel="noopener noreferrer" className={`${btn} border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20`} title="Abrir o boleto do VHSYS">
                    <Icon d={ICON_BOLETO} /> Boleto
                </a>
            )}
            {temNota && !xml && !nfErro && (
                <button onClick={buscarNota} disabled={busy} className={`${btn} border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20`} title="Buscar agora o XML da nota na NFS-e Nacional">
                    <span className={busy ? 'animate-spin' : ''}><Icon d={ICON_SYNC} /></span> {busy ? 'Buscando...' : 'Buscar NF'}
                </button>
            )}
            {nfErro && <span className="text-[11px] text-red-300 w-full">⚠ {nfErro}</span>}
            {cob.nfseErro && !xml && !msg && !nfErro && <span className="text-[11px] text-amber-300/80 w-full">⚠ {cob.nfseErro}</span>}
            {msg && <span className={`text-[11px] w-full ${msg.type === 'error' ? 'text-red-300' : 'text-emerald-300'}`}>{msg.text}</span>}
        </div>
    );
}
